/**
 * Patient domain calls (verified against cms-backend src/patients after the
 * ZOY-229 / ZOY-231 remediation).
 *
 * ONE Patient lifecycle: `POST /patients` needs only firstName + lastName +
 * contactMobile and creates a Guest (registrationState "guest", no record
 * number, gender / dateOfBirth / address may be null). A Guest can be
 * booked, rescheduled, cancelled or marked no-show, but every clinical
 * entry point refuses it with 409 PATIENT_NOT_REGISTERED. Registration is
 * the explicit `POST /patients/:id/register` act (or `completeRegistration:
 * true` on create) — it assigns the sequential record number (from 1001).
 * The strict 18/65 caregiver rule applies at registration. A suspected
 * duplicate answers 409 PATIENT_DUPLICATE_SUSPECTED with candidates; the
 * continuations are use-existing, absorb the Guest into the existing record
 * (bookings relink), or the recorded confirm-different-person resubmit.
 * No delete route exists anywhere.
 */

import { apiRequest } from '../api/http';
import { ApiError } from '../api/errors';
import { buildQueryParam, type PagedResult, type QuerySpecInput } from '../api/querySpec';

export const CAREGIVER_RELATIONSHIPS = [
  'mother',
  'father',
  'grandparent',
  'legal_guardian',
  'nanny_caretaker',
  'other',
] as const;
export type CaregiverRelationship = (typeof CAREGIVER_RELATIONSHIPS)[number];

export interface CaregiverFields {
  name: string;
  relationship: CaregiverRelationship;
  relationshipOther?: string;
  mobile: string;
  notes?: string;
  contactFirst?: boolean;
  accompanies?: boolean;
}

export interface CaregiverView {
  id: string;
  name: string;
  relationship: string;
  relationshipOther: string | null;
  mobile: string;
  notes: string | null;
  contactFirst: boolean;
  accompanies: boolean;
  removedAt: string | null;
}

export const REGISTRATION_STATES = ['guest', 'registered', 'merged'] as const;
export type RegistrationState = (typeof REGISTRATION_STATES)[number];

export interface PatientView {
  id: string;
  firstName: string;
  lastName: string;
  /** The record's primary contact mobile — the first-contact key (ZOY-229). */
  contactMobile: string;
  /** ZOY-704: optional home/landline number (canonical E.164) — searchable, never a duplicate signal. */
  homePhone: string | null;
  /** ZOY-704: optional additional reachability number — same rules as homePhone. */
  altPhone: string | null;
  /** Null until registration is completed. */
  gender: string | null;
  /** ISO date; null until registration is completed. */
  dateOfBirth: string | null;
  dobEstimated: boolean;
  /** Null until registration is completed. */
  address: string | null;
  nationalId: string | null;
  referralSource: string | null;
  notes: string | null;
  registrationState: RegistrationState;
  /**
   * The clinic currently identifies this patient as autistic (ZOY-660). A
   * clinic attribute, not a diagnosis: editable, default false, ledgered.
   */
  autismIdentified: boolean;
  registeredAt: string | null;
  registeredBy: string | null;
  /** The clinic file number (sequential from 1001) — null for a Guest (ZOY-231). */
  recordNumber: number | null;
  /** Set on an absorbed Guest: the surviving record's id. */
  mergedIntoId: string | null;
  createdBy: string;
  createdAt: string;
  /** Present only with clinic.patients.contacts.read. */
  caregivers?: CaregiverView[];
}

export interface DuplicateCandidate {
  patientId: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string | null;
  registrationState: RegistrationState;
  recordNumber: number | null;
  /** contact_mobile · caregiver_mobile · name_dob … */
  signals: string[];
}

export const PatientErrorCode = {
  NotFound: 'PATIENT_NOT_FOUND',
  CaregiverRequired: 'PATIENT_CAREGIVER_REQUIRED',
  DuplicateSuspected: 'PATIENT_DUPLICATE_SUSPECTED',
  /** Registration needs the full data set — the body lists `missing`. */
  RegistrationIncomplete: 'PATIENT_REGISTRATION_INCOMPLETE',
  AlreadyRegistered: 'PATIENT_ALREADY_REGISTERED',
  /** A Guest is bookable but is NOT a clinical record (registration gate). */
  NotRegistered: 'PATIENT_NOT_REGISTERED',
  /** An absorbed Guest: the identity lives on in `mergedIntoId`. */
  Merged: 'PATIENT_MERGED',
  AbsorbSourceNotGuest: 'PATIENT_ABSORB_SOURCE_NOT_GUEST',
  AbsorbTargetInvalid: 'PATIENT_ABSORB_TARGET_INVALID',
  AbsorbBookingOverlap: 'PATIENT_ABSORB_BOOKING_OVERLAP',
  /** A phone that is not canonical E.164 — the body's `field` names the input (contactMobile · homePhone · altPhone). */
  MobileNotCanonical: 'PATIENT_MOBILE_NOT_CANONICAL',
} as const;

/** The input a PATIENT_MOBILE_NOT_CANONICAL refusal points at, when the body names one. */
export function nonCanonicalMobileField(err: unknown): string | null {
  if (!(err instanceof ApiError) || err.code !== PatientErrorCode.MobileNotCanonical) return null;
  const field = (err.body as { field?: unknown } | undefined)?.field;
  return typeof field === 'string' ? field : null;
}

/** The registered data set the explicit registration act requires. */
export type RegistrationField = 'gender' | 'dateOfBirth' | 'address' | 'contactMobile';

export interface RegisterPatientBody {
  firstName: string;
  lastName: string;
  contactMobile: string;
  /** ZOY-704: optional, canonical E.164; omit to leave unset. */
  homePhone?: string;
  altPhone?: string;
  gender?: string;
  dateOfBirth?: string; // ISO date
  dobEstimated?: boolean;
  address?: string;
  /** Register in the same call (needs gender, dateOfBirth, address). */
  completeRegistration?: boolean;
  nationalId?: string;
  referralSource?: string;
  notes?: string;
  /** Default false when omitted. */
  autismIdentified?: boolean;
  caregivers?: CaregiverFields[];
  /** The recorded continuation past flagged duplicates (needs duplicates.resolve). */
  confirmDifferentPerson?: boolean;
}

/** `POST /patients/:id/register` — every member optional; the merge must complete the set. */
export interface CompleteRegistrationBody {
  gender?: string;
  dateOfBirth?: string;
  dobEstimated?: boolean;
  address?: string;
  contactMobile?: string;
  /** ZOY-704: a value replaces; omitted keeps the stored one (null does NOT clear on this route). */
  homePhone?: string;
  altPhone?: string;
  nationalId?: string;
  referralSource?: string;
  notes?: string;
  /** Keeps the stored value when omitted. */
  autismIdentified?: boolean;
  caregivers?: CaregiverFields[];
  confirmDifferentPerson?: boolean;
}

export interface PatientLookupResult {
  /** The normalized mobile the server matched on. */
  mobile: string;
  candidates: DuplicateCandidate[];
}

export const listPatients = (spec: QuerySpecInput) =>
  apiRequest<PagedResult<PatientView>>({
    base: 'clinic',
    path: '/v1/patients',
    query: { query: buildQueryParam(spec) },
  });

export const registerPatient = (body: RegisterPatientBody) =>
  apiRequest<PatientView>({ base: 'clinic', method: 'POST', path: '/v1/patients', body });

export const fetchPatient = (id: string) =>
  apiRequest<PatientView>({ base: 'clinic', path: `/v1/patients/${id}` });

/**
 * ZOY-640: an edit that changes an identity field (name, date of birth,
 * national ID, contact mobile) re-runs duplicate detection and may answer
 * 409 PATIENT_DUPLICATE_SUSPECTED with the creation-time candidate shape;
 * `confirmDifferentPerson: true` on the same PATCH keeps both (recorded as
 * at creation; needs duplicates.resolve).
 */
export const patchPatient = (
  id: string,
  body: Partial<
    Omit<RegisterPatientBody, 'caregivers' | 'completeRegistration' | 'homePhone' | 'altPhone'>
  > & {
    /** ZOY-704: `null` clears; omitted leaves the stored value. */
    homePhone?: string | null;
    altPhone?: string | null;
  },
) => apiRequest<PatientView>({ base: 'clinic', method: 'PATCH', path: `/v1/patients/${id}`, body });

/**
 * First contact by mobile (reception booking flow): matches the contact
 * mobile AND live caregiver mobiles; ≥5 digits (400 QUERY_INVALID below).
 */
export const lookupPatientByMobile = (mobile: string) =>
  apiRequest<PatientLookupResult>({
    base: 'clinic',
    path: '/v1/patients/lookup',
    query: { mobile },
  });

/** The explicit registration act: Guest → registered Patient with a record number. */
export const completeRegistration = (id: string, body: CompleteRegistrationBody) =>
  apiRequest<PatientView>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/patients/${id}/register`,
    body,
  });

export interface AbsorbResult {
  /** The surviving (target) record. */
  patient: PatientView;
  absorbedPatientId: string;
  relinkedBookingIds: string[];
}

/** Absorb a Guest into an existing record; its bookings relink (needs duplicates.resolve). */
export const absorbPatient = (guestId: string, intoPatientId: string) =>
  apiRequest<AbsorbResult>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/patients/${guestId}/absorb`,
    body: { intoPatientId },
  });

/** Verified: PatientChangeHistory row (B2 ledger; also carries assessment/package amendments). */
export interface PatientHistoryEntry {
  id: string;
  subjectType: string;
  subjectId: string;
  field: string;
  oldValue: string | null;
  newValue: string | null;
  changedBy: string;
  changedAt: string;
  /**
   * ZOY-683: the assessment an assessment-content entry belongs to. Null for
   * every other subject and for entries recorded before ZOY-683.
   */
  assessmentId?: string | null;
}

export const fetchPatientHistory = (id: string) =>
  apiRequest<{ items: PatientHistoryEntry[] }>({
    base: 'clinic',
    path: `/v1/patients/${id}/history`,
  });

export const addCaregiver = (patientId: string, body: CaregiverFields) =>
  apiRequest<CaregiverView>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/patients/${patientId}/caregivers`,
    body,
  });

export const patchCaregiver = (caregiverId: string, body: Partial<CaregiverFields>) =>
  apiRequest<CaregiverView>({
    base: 'clinic',
    method: 'PATCH',
    path: `/v1/patients/caregivers/${caregiverId}`,
    body,
  });

/** Soft removal — audited; reads filter removed rows. Never a delete. */
export const removeCaregiver = (caregiverId: string) =>
  apiRequest<unknown>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/patients/caregivers/${caregiverId}/remove`,
    body: {},
  });

/** INV-022 gate — derived, never stored. */
export interface AssessmentGateView {
  patientId: string;
  gateSatisfied: boolean;
  latestCompletedAssessment: {
    id: string;
    type: string;
    signedBy: string;
    signedAt: string;
  } | null;
}

export const fetchAssessmentGate = (patientId: string) =>
  apiRequest<AssessmentGateView>({
    base: 'clinic',
    path: `/v1/patients/${patientId}/assessment-gate`,
  });

export const listIntakesForPatient = (patientId: string) =>
  apiRequest<{ items: IntakeSummary[] }>({
    base: 'clinic',
    path: `/v1/patients/${patientId}/intakes`,
  });

export interface IntakeSummary {
  id: string;
  patientId: string;
  captureMode: string;
  status: string;
  createdBy: string;
  createdAt: string;
  completedBy: string | null;
  completedAt: string | null;
  /** ZOY-704: live file attachments on this intake — travels with the list, no per-row lookup. */
  attachmentCount: number;
}
